// AIchat/src/hooks/useModelsCache.js
import { useState, useEffect } from 'react';

class ModelsCache {
  constructor() {
    this.cache = new Map();
    this.timestamps = new Map();
    this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  }

  isValid(key) {
    const timestamp = this.timestamps.get(key);
    if (!timestamp) return false;
    return Date.now() - timestamp < this.CACHE_DURATION;
  }

  get(key) {
    if (this.isValid(key)) {
      return this.cache.get(key);
    }
    return null;
  }

  set(key, data) {
    this.cache.set(key, data);
    this.timestamps.set(key, Date.now());
  }

  clear(key) {
    this.cache.delete(key);
    this.timestamps.delete(key);
  }

  clearAll() {
    this.cache.clear();
    this.timestamps.clear();
  }
}

const modelsCache = new ModelsCache();

export const useModelsCache = (mode = 'chat') => {
  const [models, setModels] = useState([]);
  const [defaultModel, setDefaultModel] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchModels();
  }, [mode]);

  const fetchModels = async () => {
    const cacheKey = `models_${mode}`;
    
    // Check cache first
    const cachedData = modelsCache.get(cacheKey);
    if (cachedData) {
      setModels(cachedData.models);
      setDefaultModel(cachedData.defaultModel);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const endpoint = mode === 'vibecoding' ? '/api/models/vibecoding' : '/api/models/chat';
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3005'}${endpoint}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch models');
      }
      
      const data = await response.json();
      
      // Cache the data
      modelsCache.set(cacheKey, {
        models: data.models || [],
        defaultModel: data.defaultModel || ''
      });
      
      setModels(data.models || []);
      setDefaultModel(data.defaultModel || '');
      setError(null);
    } catch (err) {
      console.error('Error fetching models:', err);
      setError(err.message);
      setModels([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshModels = () => {
    modelsCache.clear(`models_${mode}`);
    fetchModels();
  };

  return {
    models,
    defaultModel,
    loading,
    error,
    refreshModels
  };
};

export default modelsCache;